import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissionContext } from '../../contexts/PermissionContext'
import { mfaApi } from '../../services/api/mfa'
import { useAppPath } from '../../hooks/useAppPath'

const DISMISS_KEY = 'mfa_banner_dismissed_until'
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000

function isDismissed(): boolean {
  const until = localStorage.getItem(DISMISS_KEY)
  if (!until) return false
  return Date.now() < parseInt(until, 10)
}

function dismiss(): void {
  localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS))
}

export const MfaEnrollBanner: React.FC = () => {
  const { user, isAuthenticated } = useAuth()
  const { isPlatformAdmin } = usePermissionContext()
  const appPath = useAppPath()
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(isDismissed)

  const privileged = isPlatformAdmin || (user?.isAdmin ?? false)

  useEffect(() => {
    if (!isAuthenticated || !user || dismissed) return
    if (!privileged) return

    mfaApi.getStatus().then((status) => {
      if (!status.enrolled) setVisible(true)
    }).catch(() => {})
  }, [isAuthenticated, user, privileged, dismissed])

  const handleDismiss = () => {
    dismiss()
    setDismissed(true)
    setVisible(false)
  }

  if (!visible || dismissed) return null

  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700/50"
    >
      <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>
          Multi-factor authentication is required for your role.{' '}
          <Link
            to={appPath('/profile?tab=security')}
            className="font-semibold underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200"
          >
            Enroll now
          </Link>
        </span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss MFA enrollment banner"
        className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-800/40 transition-colors flex-shrink-0"
      >
        <X className="h-4 w-4 text-amber-700 dark:text-amber-400" />
      </button>
    </div>
  )
}
